import {
  Button,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import numeral from 'numeral'
import { FC, Fragment, useEffect } from 'react'

import {
  Fee,
  Maybe,
  MembershipInterval,
  PaymentDetailFragment,
  PaymentMethod,
} from '@/clients/utoniq-core/schema'
import { LegalInformationLinksCell } from '@/components/organisms/legalInformation'
import {
  CarrierPaymentOption,
  CreditCardPayment,
} from '@/components/organisms/payment'
import { config } from '@/config'
import { useForm, UseFormProps, useRouter, useTranslation } from '@/core'
import { usePrivateAction } from '@/lib/privateAction'

type ArtistMembershipPurchaseFormProps = {
  plans: MembershipPlan[]
  initialPlan?: ArtistMembershipPurchaseValues
  paymentSource?: Maybe<PaymentDetailFragment>
  paymentFees?: Maybe<Fee[]>
  onSubmit: (values: ArtistMembershipPurchaseValues) => Promise<void>
  /** アーティスト毎に設定されているメンバーシップのキャリア決済加入が有効かどうか */
  enableMembershipCarrierPayment: boolean
}

export type ArtistMembershipPurchaseValues = {
  interval: MembershipInterval
  paymentMethod: PaymentMethod
}

export type ArtistMembershipPurchaseInput = ArtistMembershipPurchaseValues & {
  planId: string
}

export type MembershipPlan = {
  interval: MembershipInterval
  amount: number
  formattedAmount: string
  note?: string
  annualDiscountText?: string
  planId: string
}

const schema: UseFormProps['schema'] = o => ({
  interval: o.string(),
  paymentMethod: o.string(),
})

export const ArtistMembershipPurchaseForm: FC<ArtistMembershipPurchaseFormProps> =
  ({
    plans,
    initialPlan,
    paymentSource,
    paymentFees,
    onSubmit,
    enableMembershipCarrierPayment,
  }) => {
    const { t } = useTranslation('artistMembershipPurchaseProceed')
    const { router, routerPages } =
      useRouter<{ artistId: string; productId: string }>()
    const { handleLoginModal } = usePrivateAction()

    const {
      handleSubmit,
      formState: { isSubmitting },
      watch,
      setValue,
      reset,
    } = useForm<ArtistMembershipPurchaseValues>({
      schema,
      defaultValues,
    })

    const intervalWatch = watch('interval')
    const paymentMethodWatch = watch('paymentMethod')

    useEffect(() => {
      if (!plans.length) {
        return
      }
      const yearPlan = plans.find(
        plan => plan.interval === MembershipInterval.Year
      )
      if (!yearPlan) {
        reset({
          interval: MembershipInterval.Month,
          paymentMethod: PaymentMethod.CreditCard,
        })
      }
    }, [plans])

    useEffect(() => {
      if (initialPlan) {
        reset(initialPlan)
      }
    }, [initialPlan])

    const handleOnSubmit = handleSubmit(async values => {
      await onSubmit(values)
    })

    return (
      <Stack>
        <Stack spacing={5} pb={5} pt={2}>
          <Typography
            variant="h5"
            sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}
            color="text.secondary"
          >
            {t('course')}
          </Typography>
          <RadioGroup
            value={intervalWatch}
            onChange={(event, value) => {
              handleLoginModal(() => {
                setValue('interval', value as never, {
                  shouldValidate: true,
                })
                if (value === 'month') {
                  setValue('paymentMethod', PaymentMethod.CreditCard)
                }
              })(event as never)
            }}
            sx={{ gap: theme => theme.spacing(4), px: 5 }}
          >
            {plans.map((plan, index) => (
              <Fragment key={plan.interval}>
                <FormControlLabel
                  value={plan.interval}
                  checked={intervalWatch === plan.interval}
                  control={<Radio />}
                  label={
                    <Stack sx={{ gap: 1 }}>
                      <Typography variant="h5">
                        {t(`${plan.interval}Course`)}
                      </Typography>
                      <Stack direction="row" alignItems="center">
                        <Typography variant="subtitle1">
                          {t(`${plan.interval}CoursePrice`, {
                            amount: plan.formattedAmount,
                          })}
                        </Typography>
                        {plan.interval === MembershipInterval.Year && (
                          <Typography variant="body2">
                            (
                            {t('monthCoursePrice', {
                              amount: numeral(
                                Math.ceil(plan.amount / 12)
                              ).format('0,0'),
                            })}
                            )
                          </Typography>
                        )}
                        {plan.interval === MembershipInterval.Year &&
                          !!plan.annualDiscountText && (
                            <Typography
                              variant="subtitle2"
                              color="primary.main"
                              sx={{ ml: 1 }}
                            >
                              {plan.annualDiscountText}
                            </Typography>
                          )}
                      </Stack>
                    </Stack>
                  }
                />
                {plans.length - 1 > index && <Divider />}
              </Fragment>
            ))}
          </RadioGroup>
        </Stack>
        <Stack spacing={5} pb={5}>
          <Typography
            variant="h5"
            sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}
            color="text.secondary"
          >
            {t('method', '支払い方法')}
          </Typography>
          {/* 月払いの場合クレジットカード決済もしくはキャリア決済が選択できる */}
          {intervalWatch === MembershipInterval.Month ? (
            <RadioGroup
              value={paymentMethodWatch}
              onChange={(event, value) => {
                handleLoginModal(() => {
                  setValue('paymentMethod', value as PaymentMethod, {
                    shouldValidate: true,
                  })
                })(event as never)
              }}
              sx={{ gap: theme => theme.spacing(4), px: 5 }}
            >
              <FormControlLabel
                value={PaymentMethod.CreditCard}
                checked={paymentMethodWatch === PaymentMethod.CreditCard}
                control={<Radio />}
                label={
                  <CreditCardPayment
                    paymentSource={paymentSource}
                    goBackPath={routerPages.ArtistMembershipPurchaseProceed}
                    queryParams={{
                      artistId:
                        (config.applicationType === 'standalone'
                          ? config.artistId
                          : router.query.artistId) ?? '#',
                      productId: router.query.productId,
                    }}
                  />
                }
              />
              {/*
               * アーティスト毎に設定されているメンバーシップのキャリア決済加入が有効な場合のみ
               * キャリア決済の選択肢を表示する
               * ref. https://app.clickup.com/t/908127/UT-20671
               */}
              {enableMembershipCarrierPayment && (
                <>
                  <Divider />
                  <Stack>
                    <Typography variant="h5">
                      {t('carrier', 'キャリア決済')}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ pt: 2 }}
                    >
                      {t('carrierDesc', '携帯料金と一緒にお支払いできます')}
                    </Typography>
                  </Stack>
                  <Stack>
                    <FormControlLabel
                      value={PaymentMethod.Au}
                      checked={paymentMethodWatch === PaymentMethod.Au}
                      control={<Radio />}
                      label={<CarrierPaymentOption carrier={t('au', 'au')} />}
                    />
                    <FormControlLabel
                      value={PaymentMethod.Softbank}
                      checked={paymentMethodWatch === PaymentMethod.Softbank}
                      control={<Radio />}
                      label={
                        <CarrierPaymentOption
                          carrier={t('softbank', 'Softbank')}
                        />
                      }
                    />
                    <FormControlLabel
                      value={PaymentMethod.Docomo}
                      checked={paymentMethodWatch === PaymentMethod.Docomo}
                      control={<Radio />}
                      label={
                        <CarrierPaymentOption carrier={t('docomo', 'docomo')} />
                      }
                    />
                  </Stack>
                </>
              )}
            </RadioGroup>
          ) : (
            // 年払いの場合クレジットカード決済もしくはコンビニ決済が選択できる
            <RadioGroup
              value={paymentMethodWatch}
              onChange={(event, value) => {
                handleLoginModal(() => {
                  setValue('paymentMethod', value as PaymentMethod, {
                    shouldValidate: true,
                  })
                })(event as never)
              }}
              sx={{ gap: theme => theme.spacing(4), px: 5 }}
            >
              <FormControlLabel
                value={PaymentMethod.CreditCard}
                checked={paymentMethodWatch === PaymentMethod.CreditCard}
                control={<Radio />}
                label={
                  <CreditCardPayment
                    paymentSource={paymentSource}
                    goBackPath={routerPages.ArtistMembershipPurchaseProceed}
                    queryParams={{
                      artistId:
                        (config.applicationType === 'standalone'
                          ? config.artistId
                          : router.query.artistId) ?? '#',
                      productId: router.query.productId,
                    }}
                  />
                }
              />
              <FormControlLabel
                value={PaymentMethod.Konbini}
                checked={paymentMethodWatch === PaymentMethod.Konbini}
                control={<Radio />}
                label={<KonbiniPayment paymentFees={paymentFees} />}
              />
            </RadioGroup>
          )}
        </Stack>
        <LegalInformationLinksCell sx={{ px: 5 }} />
        <Stack sx={{ my: 5 }} alignItems="center" gap={4}>
          <Button
            size="large"
            disabled={
              isSubmitting ||
              (paymentMethodWatch === PaymentMethod.CreditCard &&
                !paymentSource)
            }
            loading={isSubmitting}
            sx={{ width: 212 }}
            onClick={handleOnSubmit}
          >
            {t('submit', '確認画面に進む')}
          </Button>
          <Button
            variant="text"
            size="small"
            sx={{ width: 212 }}
            onClick={() => router.goBack()}
          >
            {t('back', '戻る')}
          </Button>
        </Stack>
      </Stack>
    )
  }

type KonbiniPaymentProps = {
  paymentFees: ArtistMembershipPurchaseFormProps['paymentFees']
}
const KonbiniPayment: FC<KonbiniPaymentProps> = ({ paymentFees }) => {
  const { t } = useTranslation('artistMembershipPurchaseProceed')

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h5">
          {t('convenienceStore', 'コンビニ決済')}
        </Typography>
      </Stack>
      <Typography variant="caption">
        {t(
          'convenienceStoreDesc',
          'ファミリーマート、ローソン、ミニストップ、セイコーマートに対応しています。'
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t(
          'convenienceStoreDesc2',
          '※コンビニ決済の振り込み期限は3日間となります。'
        )}
      </Typography>
    </Stack>
  )
}

const defaultValues: ArtistMembershipPurchaseValues = {
  interval: MembershipInterval.Year,
  paymentMethod: PaymentMethod.CreditCard,
}
